import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ShieldCheck, GraduationCap, Users, ArrowRight } from "lucide-react";
import { ConsultationForm } from "@/components/ConsultationForm";
import { MaskedLine } from "@/components/Reveal";
import { BUSINESS } from "@/lib/constants";

const trustPoints = [
  { icon: GraduationCap, label: "Personalized Education", testId: "trust-point-personalized-education" },
  { icon: Users, label: "One-on-One Guidance", testId: "trust-point-one-on-one" },
  { icon: ShieldCheck, label: "No Guaranteed Outcomes", testId: "trust-point-no-guarantees" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -48]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section ref={ref} id="home" data-testid="hero-section" className="relative overflow-hidden bg-[#141419]">
      <motion.div style={{ y: bgY }} className="hero-grid-bg absolute inset-0" aria-hidden="true" />
      <div
        className="absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-[#C9A227]/10 blur-3xl"
        aria-hidden="true"
      />
      <div className="relative mx-auto grid max-w-7xl gap-12 px-4 pb-14 pt-12 sm:px-6 lg:grid-cols-2 lg:items-center lg:gap-16 lg:px-8 lg:pb-20 lg:pt-16">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <span
              data-testid="hero-badge"
              className="inline-flex items-center gap-2 rounded-full border border-[#C9A227]/30 bg-[#17171C] px-4 py-1.5 text-xs font-semibold tracking-wide text-[#E4C463] shadow-sm"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#C9A227]" />
              Credit Education &amp; Financial Mentorship
            </span>
          </motion.div>
          <h1
            data-testid="hero-headline"
            className="mt-7 font-heading text-4xl font-bold leading-[1.08] tracking-tight text-[#F2EEE3] sm:text-5xl lg:text-6xl"
          >
            <MaskedLine delay={0.15}>Understand Credit.</MaskedLine>
            <MaskedLine delay={0.28} className="text-[#D8B54A]">Build Smarter.</MaskedLine>
            <MaskedLine delay={0.41}>Become Funding Ready.</MaskedLine>
          </h1>
          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 max-w-xl text-base leading-relaxed text-[#B8B2A6] sm:text-lg"
          >
            {BUSINESS.shortName} provides credit education, financial literacy coaching and
            personalized mentorship designed to help individuals and entrepreneurs better understand
            credit, develop stronger financial habits and prepare for future funding opportunities.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.68, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <Link
              to="/#consultation"
              data-testid="hero-get-consultation-btn"
              className="group inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-gradient-to-b from-[#E7C766] to-[#AE8924] px-7 text-sm font-semibold text-black shadow-md shadow-[#C9A227]/20 transition-colors duration-200 hover:from-[#F2D47E] hover:to-[#C49B2E]"
            >
              Start With a Free Consultation
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a
              href={BUSINESS.phoneHref}
              data-testid="hero-call-btn"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-white/25 bg-transparent px-7 text-sm font-semibold text-[#F2EEE3] transition-colors duration-200 hover:border-[#C9A227]/50 hover:bg-[#141419]"
            >
              <Phone className="h-4 w-4 text-[#D8B54A]" />
              Call {BUSINESS.phone}
            </a>
          </motion.div>
          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-x-8"
          >
            {trustPoints.map((point) => (
              <li
                key={point.label}
                data-testid={point.testId}
                className="flex items-center gap-2.5 text-sm font-medium text-[#B8B2A6]"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#C9A227]/10">
                  <point.icon className="h-3.5 w-3.5 text-[#D8B54A]" strokeWidth={2} />
                </span>
                {point.label}
              </li>
            ))}
          </motion.ul>
        </div>
        <motion.div
          id="consultation"
          style={{ y: cardY }}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="scroll-mt-28"
        >
          <ConsultationForm source="homepage-consultation-form" />
        </motion.div>
      </div>
    </section>
  );
};
